import { useRecordContext, useTranslate } from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { ShowButton } from "@/components/admin/show-button";

import { CallButton, CallsList } from "../calls";
import { MailSection } from "../mail";
import { AddTask } from "../tasks/AddTask";
import { TasksIterator } from "../tasks/TasksIterator";
import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { AsideSection } from "../misc/AsideSection";
import type { Contact } from "../types";
import { ContactMergeButton } from "./ContactMergeButton";
import { ExportVCardButton } from "./ExportVCardButton";
import { CanExport } from "../misc/CanExport";
import { CanDelete } from "../misc/CanDelete";
import {
  FromBitrixBadge,
  NotFromBitrix,
  useReadOnly,
} from "../misc/fromBitrix";

export const ContactAside = ({ link = "edit" }: { link?: "edit" | "show" }) => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const readOnly = useReadOnly(record);

  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 text-sm">
      <div className="mb-4 -ml-1 flex items-center gap-2">
        {link === "edit" ? (
          // a Bitrix24 contact in Bitrix24 mode is changed in Bitrix24
          <NotFromBitrix record={record}>
            <EditButton label="resources.contacts.action.edit" />
          </NotFromBitrix>
        ) : (
          <ShowButton label="resources.contacts.action.show" />
        )}
        <FromBitrixBadge record={record} />
      </div>

      <AsideSection title={translate("resources.notes.fields.status")}>
        <ContactStatusSelector />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.personal_info")}
      >
        <ContactPersonalInfo />
      </AsideSection>

      <AsideSection
        title={translate("resources.contacts.field_categories.background_info")}
      >
        <ContactBackgroundInfo />
      </AsideSection>

      {link === "edit" ? (
        // the contact's own page (the edit page has its own aside)
        <AsideSection title={translate("crm.calls.title")}>
          <CallButton
            phone={record.phone_jsonb?.find((p) => p?.number?.trim())?.number}
            className="self-start"
          />
          <CallsList filter={{ contact_id: record.id }} />
        </AsideSection>
      ) : null}

      {link === "edit" ? (
        <AsideSection title={translate("crm.mail.title")}>
          <MailSection
            card={{ contactId: record.id }}
            defaultTo={record.email_jsonb?.find((e) => e?.email?.trim())?.email}
            title={false}
          />
        </AsideSection>
      ) : null}

      <AsideSection
        title={translate("resources.tasks.name", { smart_count: 2 })}
      >
        <ReferenceManyField
          target="contact_id"
          reference="tasks"
          sort={{ field: "due_date", order: "ASC" }}
          perPage={1000}
        >
          <TasksIterator />
        </ReferenceManyField>
        <AddTask />
      </AsideSection>

      {link !== "edit" && !readOnly && (
        <>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <CanExport>
              <ExportVCardButton />
            </CanExport>
            <ContactMergeButton />
          </div>
          <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
            <CanDelete resource="contacts">
              <DeleteButton
                className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
                size="sm"
              />
            </CanDelete>
          </div>
        </>
      )}
    </div>
  );
};
